/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useState, useEffect} from 'react'
import {Link} from 'react-router-dom'
import {useAuth} from '../../../../app/modules/auth'
import {avatarPath} from '../../../helpers'
import {TOKENS} from '../../../../app/components/design-system'

type MenuRowProps = {
  to?: string
  onClick?: () => void
  children: React.ReactNode
  tone?: 'default' | 'danger'
}

// Single tokenized row component used for every menu link inside the
// dropdown. Hover state changes the row background to the surface token
// so the dropdown feels consistent with sensors-v2 toolbars and the
// company picker.
const MenuRow: FC<MenuRowProps> = ({to, onClick, children, tone = 'default'}) => {
  const [hover, setHover] = useState(false)
  const color = tone === 'danger' ? TOKENS.color.severity.high.text : TOKENS.color.text
  const baseStyle: React.CSSProperties = {
    display: 'block',
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 500,
    color,
    borderRadius: TOKENS.radius.chip,
    background: hover ? TOKENS.color.surface : 'transparent',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background 120ms ease',
  }
  const handlers = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  }
  if (to) {
    return <Link to={to} style={baseStyle} {...handlers}>{children}</Link>
  }
  return (
    <a onClick={onClick} style={baseStyle} {...handlers}>{children}</a>
  )
}

const HeaderUserMenu: FC = () => {
  const {currentUser, logout} = useAuth()
  const [avatar, setAvatar] = useState('')

  useEffect(() => {
    setAvatar(avatarPath(currentUser?.avatar_url, currentUser?.avatar))
  }, [currentUser])

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-rounded'
      data-kt-menu='true'
      style={{
        background: TOKENS.color.card,
        border: `1px solid ${TOKENS.color.border}`,
        borderRadius: TOKENS.radius.card,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        width: 280,
        padding: 6,
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: TOKENS.spacing.md,
        padding: '12px 10px',
        borderBottom: `1px solid ${TOKENS.color.border}`,
        marginBottom: 4,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          overflow: 'hidden', flexShrink: 0,
          background: TOKENS.color.surface,
          border: `1px solid ${TOKENS.color.border}`,
        }}>
          <img
            alt={currentUser?.name}
            src={avatar}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            fontWeight: 700,
            color: TOKENS.color.text,
            minWidth: 0,
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentUser?.name}
            </span>
            <span style={{
              background: TOKENS.color.severity.low.bg,
              color: TOKENS.color.severity.low.text,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              padding: '2px 6px',
              borderRadius: TOKENS.radius.chip,
              flexShrink: 0,
            }}>
              Admin
            </span>
          </div>
          <a
            href={`mailto://${currentUser?.email}`}
            style={{
              fontSize: 11,
              color: TOKENS.color.muted,
              textDecoration: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentUser?.email}
          </a>
        </div>
      </div>

      <MenuRow to='/cmb/account/settings'>Preferências do Usuário</MenuRow>
      <MenuRow to='/tutorial-websocket'>Tutorial WebSocket</MenuRow>

      <div style={{ height: 1, background: TOKENS.color.border, margin: '6px 4px' }} />

      <MenuRow onClick={logout} tone='danger'>Sair</MenuRow>
    </div>
  )
}

export {HeaderUserMenu}
